'use client';

import { ReactNode } from 'react';
import { DateTime } from 'luxon';
import {
  AlertCircle,
  CalendarClock,
  ChefHat,
  CheckCircle2,
  Clock,
  House,
  Truck,
  Utensils,
} from 'lucide-react';
import { DeliveryStatus, OrderStatus } from '@instamenu/data-access';
import type { Order } from '@instamenu/context-checkout';

import { deliveryStatusMap, getCustomerFacingDeliveryStatus } from '../delivery-tracker/status-map';
import type { StatusPillTone } from './components';

export interface StatusPillContent {
  label: string;
  icon: ReactNode;
  tone: StatusPillTone;
}

const ICON_SIZE = 12;
const STROKE = 2.5;

/**
 * Hero pill for a Pickup order. Scheduled orders surface the booked time
 * ("Scheduled for 5:30 PM" today, "Scheduled for Mar 15, 5:30 PM" other
 * day). Otherwise we fall back to the restaurant's current wait estimate.
 *
 * Note: this label does NOT count down as time passes. If we ever want a
 * live countdown, do it here (not in the screen component).
 */
export const getPickupPillContent = (order: Order): StatusPillContent => {
  if (order.scheduledAt) {
    const time = order.scheduledAt.toFormat('t'); // e.g. "5:30 PM"
    const sameDay = DateTime.now().hasSame(order.scheduledAt, 'day');
    const label = sameDay
      ? `Scheduled for ${time}`
      : `Scheduled for ${order.scheduledAt.toFormat('MMM d')}, ${time}`;
    return {
      label,
      icon: <CalendarClock size={ICON_SIZE} strokeWidth={STROKE} />,
      tone: 'warm',
    };
  }
  const wait = order.restaurant.waitTime;
  return {
    label: wait ? `Ready in ~${wait} min` : 'Ready soon',
    icon: <Clock size={ICON_SIZE} strokeWidth={STROKE} />,
    tone: 'warm',
  };
};

/**
 * Hero pill for a delivery order. Labels come straight from
 * `deliveryStatusMap` (the source of truth shared with `DeliveryTracker`),
 * so the pill and timeline can never disagree. This helper only owns the
 * icon + tone choice, which is UI-specific to the hero chip.
 */
export const getDeliveryPillContent = (order: Order): StatusPillContent => {
  const customerFacing = getCustomerFacingDeliveryStatus(order.delivery?.status);
  const info = deliveryStatusMap(order.status, customerFacing);

  if (info.isError) {
    return {
      label: info.label,
      icon: <AlertCircle size={ICON_SIZE} strokeWidth={STROKE} />,
      tone: 'error',
    };
  }

  switch (customerFacing) {
    case DeliveryStatus.Delivered:
      return {
        label: info.label,
        icon: <CheckCircle2 size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'success',
      };
    case DeliveryStatus.DropoffEnroute:
    case DeliveryStatus.PickupEnroute:
      return {
        label: info.label,
        icon: <Truck size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'delivery',
      };
    case DeliveryStatus.AtPickup:
      return {
        label: info.label,
        icon: <House size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'delivery',
      };
    case DeliveryStatus.Submitted:
      return {
        label: info.label,
        icon: <ChefHat size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'confirmed',
      };
    default:
      // OrderStatus.Placed / Accepted / Created — waiting on the restaurant.
      return {
        label: info.label,
        icon: <Clock size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'confirmed',
      };
  }
};

/**
 * Hero pill for a dine-in (Table) order. The backend doesn't expose a
 * fine-grained "BeingPrepared" status for in-house orders, so we lean on
 * the raw `OrderStatus` enum: Received → Being prepared → Served.
 * Custom labels here because the platform-wide `ORDER_STATUS_MAP` labels
 * ("Placed", "Accepted") are too terse for a customer-facing hero pill.
 */
export const getTablePillContent = (order: Order): StatusPillContent => {
  if (
    order.scheduledAt &&
    order.status !== OrderStatus.Canceled &&
    order.status !== OrderStatus.Completed
  ) {
    return getPickupPillContent(order);
  }

  switch (order.status) {
    case OrderStatus.Canceled:
      return {
        label: 'Canceled',
        icon: <AlertCircle size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'error',
      };
    case OrderStatus.Completed:
      return {
        label: order.isPickupLocation ? 'Ready for pickup' : 'Served',
        icon: order.isPickupLocation ? (
          <CheckCircle2 size={ICON_SIZE} strokeWidth={STROKE} />
        ) : (
          <Utensils size={ICON_SIZE} strokeWidth={STROKE} />
        ),
        tone: 'success',
      };
    case OrderStatus.Accepted:
      return {
        label: 'Being prepared',
        icon: <ChefHat size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'confirmed',
      };
    case OrderStatus.Placed:
    case OrderStatus.Created:
    default:
      return {
        label: 'Order received',
        icon: <Clock size={ICON_SIZE} strokeWidth={STROKE} />,
        tone: 'confirmed',
      };
  }
};
